import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import plataformasContext from '../../context/plataformas/plataformasContext';
import Routes from '../../utils/Routes';

function Plataformas() {
    const navigate = useNavigate();
    const pContext = useContext(plataformasContext);
    const { plataformas, getAllPlataformas, addPlataforma, updatePlataforma, deletePlataforma } = pContext;

    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentPlataforma, setCurrentPlataforma] = useState({
        codPlataforma: '',
        name: '',
        link: '',
        enabled: true
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await getAllPlataformas();
            setLoading(false);
        };
        fetchData();
    }, []);

    const handleOpenDialog = (plataforma = null) => {
        if (plataforma) {
            setEditMode(true);
            setCurrentPlataforma({ ...plataforma });
        } else {
            setEditMode(false);
            setCurrentPlataforma({
                codPlataforma: '',
                name: '',
                link: '',
                enabled: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentPlataforma({
            codPlataforma: '',
            name: '',
            link: '',
            enabled: true
        });
    };

    const handleSave = async () => {
        try {
            if (editMode) {
                await updatePlataforma(currentPlataforma);
                Swal.fire('Exito', 'Plataforma actualizada correctamente', 'success');
            } else {
                await addPlataforma(currentPlataforma);
                Swal.fire('Exito', 'Plataforma creada correctamente', 'success');
            }
            handleCloseDialog();
            await getAllPlataformas();
        } catch (error) {
            Swal.fire('Error', error.message || 'Error al guardar plataforma', 'error');
        }
    };

    const handleDelete = async (plataforma) => {
        const result = await Swal.fire({
            title: 'Confirmar eliminacion',
            text: `Esta seguro de eliminar la plataforma ${plataforma.codPlataforma}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Si, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deletePlataforma(plataforma);
                Swal.fire('Eliminado', 'Plataforma eliminada correctamente', 'success');
                await getAllPlataformas();
            } catch (error) {
                Swal.fire('Error', error.message || 'Error al eliminar plataforma', 'error');
            }
        }
    };

    const handleOpenLink = (link) => {
        if (link) {
            window.open(link, '_blank');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(Routes.abms)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ color: '#3d4d87', fontWeight: 'bold', flex: 1 }}>
                    Plataformas
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ backgroundColor: '#3d4d87' }}
                >
                    Nueva Plataforma
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>Codigo</TableCell>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Link</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {plataformas?.map((plataforma) => (
                            <TableRow key={plataforma._id} hover>
                                <TableCell>{plataforma.codPlataforma}</TableCell>
                                <TableCell>{plataforma.name}</TableCell>
                                <TableCell>
                                    {plataforma.link ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block'
                                            }}>
                                                {plataforma.link}
                                            </span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenLink(plataforma.link)}
                                                color="primary"
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : '-'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={plataforma.enabled ? 'Activo' : 'Inactivo'}
                                        color={plataforma.enabled ? 'success' : 'error'}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton onClick={() => handleOpenDialog(plataforma)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(plataforma)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editMode ? 'Editar Plataforma' : 'Nueva Plataforma'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Codigo de la Plataforma"
                            value={currentPlataforma.codPlataforma}
                            onChange={(e) => setCurrentPlataforma({ ...currentPlataforma, codPlataforma: e.target.value.toUpperCase() })}
                            fullWidth
                            required
                            disabled={editMode}
                        />
                        <TextField
                            label="Nombre"
                            value={currentPlataforma.name}
                            onChange={(e) => setCurrentPlataforma({ ...currentPlataforma, name: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Link"
                            value={currentPlataforma.link}
                            onChange={(e) => setCurrentPlataforma({ ...currentPlataforma, link: e.target.value })}
                            fullWidth
                            placeholder="https://..."
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={currentPlataforma.enabled}
                                    onChange={(e) => setCurrentPlataforma({ ...currentPlataforma, enabled: e.target.checked })}
                                />
                            }
                            label="Activo"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#3d4d87' }}>
                        {editMode ? 'Guardar Cambios' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Plataformas;
